import React, { Dispatch, useEffect, useRef, useState } from "react";
import { CameraType, Camera } from "react-camera-pro";
import jsQR from "jsqr";
import { StyledQrScanner } from "./StyledQrScanner.styled";
import { Box, Button, Typography } from "@mui/material";
import { CameraRotateIcon } from "@phosphor-icons/react";

type QrScannerProps = {
  setCode: Dispatch<React.SetStateAction<string>>;
};

export const QrScanner = ({ setCode }: QrScannerProps) => {
  const camera = useRef<CameraType>(null);
  const canvasRef = useRef<HTMLCanvasElement>(document.createElement("canvas"));
  const processingRef = useRef(false);

  useEffect(() => {
    let stopped = false;
    const tick = () => {
      if (stopped) return;
      scanFrame();
      setTimeout(tick, 300);
    };
    tick();
    return () => {
      stopped = true;
    };
  }, []);

  const scanFrame = () => {
    if (!camera.current) return;

    if (processingRef.current) return; // ✅ avoid overlapping frames
    processingRef.current = true;

    try {
      const photo = camera.current.takePhoto(); // <-- this is what throws for you
      if (typeof photo !== "string") return;

      const image = new Image();
      image.onload = () => {
        const canvas = canvasRef.current;
        const scale = 0.5;

        canvas.width = image.width * scale;
        canvas.height = image.height * scale;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height);
        if (code?.data) {
          setCode(code.data);
        }
      };

      image.src = photo;
    } catch (e) {
      console.error("takePhoto failed:", e);
    } finally {
      processingRef.current = false;
    }
  };

  return (
    <StyledQrScanner>
      <Typography variant="h2">QR Code scannen</Typography>
      <Box className="cameraWrapper">
        <Box className="camera">
          <Camera
            ref={camera}
            aspectRatio="cover"
            facingMode="environment"
            errorMessages={{
              noCameraAccessible: undefined,
              permissionDenied: undefined,
              switchCamera: undefined,
              canvas: undefined,
            }}
          />
        </Box>

        <Box className="camerabrackets">
          <Box className="bracket-1 topleft" />
          <Box className="bracket-2 topleft" />

          <Box className="bracket-1 topright" />
          <Box className="bracket-2 topright" />

          <Box className="bracket-1 bottomleft" />
          <Box className="bracket-2 bottomleft" />

          <Box className="bracket-1 bottomright" />
          <Box className="bracket-2 bottomright" />
        </Box>
      </Box>
    </StyledQrScanner>
  );
};
